import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Upload, Eye, Download, Pencil, Trash2, Loader2, Receipt as ReceiptIcon } from 'lucide-react';
import { toast } from 'sonner';
import { receiptsApi } from '../api';
import { fmtDate, centsToDollarString, dollarsToCents, centsToDollarInput } from '../constants';
import { MoneyInput } from './MoneyInput';
import { ReceiptThumb, ReceiptPreviewDialog, downloadReceipt } from './ReceiptPreviewDialog';
import { ReceiptUploadDialog, RECEIPT_PAYMENT_OPTIONS } from './ReceiptUploadDialog';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls =
  'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

const pmLabel = (v) => (RECEIPT_PAYMENT_OPTIONS.find((o) => o.value === v) || {}).label || v || '—';

const ReceiptEditDialog = ({ open, onOpenChange, receipt, onSaved }) => {
  const [form, setForm] = useState({ amount: '', purchased_by: '', purchased_where: '', payment_method: 'card', note: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && receipt) {
      setForm({
        amount: centsToDollarInput(receipt.amount_cents),
        purchased_by: receipt.purchased_by || '',
        purchased_where: receipt.purchased_where || '',
        payment_method: receipt.payment_method || 'card',
        note: receipt.note || '',
      });
    }
  }, [open, receipt]);

  const save = async () => {
    const cents = dollarsToCents(form.amount);
    if (cents == null || cents <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.purchased_by.trim()) { toast.error('Enter who made the purchase'); return; }
    if (!form.purchased_where.trim()) { toast.error('Enter where it was purchased'); return; }
    setSaving(true);
    try {
      const res = await receiptsApi.update(receipt.id, {
        amount_cents: cents,
        purchased_by: form.purchased_by.trim(),
        purchased_where: form.purchased_where.trim(),
        payment_method: form.payment_method,
        note: form.note.trim() || null,
      });
      toast.success('Receipt updated — costs recalculated');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update receipt');
    } finally { setSaving(false); }
  };

  if (!receipt) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-md" data-testid="receipt-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Receipt</DialogTitle>
          <DialogDescription className="text-gray-500">Changes update the linked project cost automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Amount ($)</label>
              <MoneyInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} className={inputCls} data-testid="re-amount" />
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Payment Method</label>
              <select value={form.payment_method} onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))} className={selectCls} data-testid="re-payment-method">
                {RECEIPT_PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">Purchased By</label>
            <Input value={form.purchased_by} onChange={(e) => setForm((f) => ({ ...f, purchased_by: e.target.value }))} className={inputCls} data-testid="re-purchased-by" />
          </div>
          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">Purchased Where</label>
            <Input value={form.purchased_where} onChange={(e) => setForm((f) => ({ ...f, purchased_where: e.target.value }))} className={inputCls} data-testid="re-purchased-where" />
          </div>
          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">Note</label>
            <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className={inputCls} data-testid="re-note" />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-800">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="border-gray-700 text-gray-300" data-testid="re-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="re-save">
            {saving && <Loader2 size={15} className="animate-spin" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Dedicated receipt area inside Project Financials — the ONLY place receipts live.
export const ProjectReceiptsSection = ({ project, onFinancialsChanged }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const locked = Boolean(project.is_archived || project.is_financially_closed);

  const load = useCallback(() => {
    receiptsApi.list({ project_id: project.id })
      .then((r) => setReceipts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); onFinancialsChanged?.(); };

  const confirmDelete = async () => {
    try {
      await receiptsApi.delete(deleting.id);
      toast.success('Receipt deleted — costs recalculated');
      setDeleting(null);
      changed();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete receipt');
    }
  };

  const total = receipts.reduce((s, r) => s + (r.amount_cents || 0), 0);

  return (
    <Card className="bg-[#111111] border-gray-800 overflow-hidden" data-testid="project-receipts-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-gray-800">
        <div>
          <h4 className="text-sm font-semibold text-white">Receipts</h4>
          <p className="text-[11px] text-gray-600 mt-0.5">
            {receipts.length > 0
              ? `${receipts.length} receipt${receipts.length === 1 ? '' : 's'} · ${centsToDollarString(total)} in Materials/Misc`
              : 'Every receipt uploaded here feeds Materials/Misc costs above.'}
          </p>
        </div>
        {!locked && (
          <Button onClick={() => setUploadOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold shrink-0" data-testid="project-receipts-upload-btn">
            <Upload size={15} /> Upload Receipt
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500" data-testid="project-receipts-loading">
          <Loader2 size={20} className="mx-auto mb-2 animate-spin text-[#D4AF37]" /> Loading receipts…
        </div>
      ) : receipts.length === 0 ? (
        <div className="text-center py-10 px-4" data-testid="project-receipts-empty">
          <ReceiptIcon size={28} className="mx-auto mb-2 text-gray-700" />
          <p className="text-sm text-gray-500">No receipts uploaded for this project yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
          {receipts.map((r) => (
            <div key={r.id} className="flex gap-3 bg-[#161616] border border-gray-800 rounded-lg p-3" data-testid={`receipt-card-${r.id}`}>
              <button onClick={() => setPreview(r)} className="shrink-0" title="Preview">
                <ReceiptThumb receipt={r} className="w-16 h-16 rounded-md" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[#D4AF37] font-semibold text-sm">{centsToDollarString(r.amount_cents)}</span>
                  <span className="text-[10px] text-gray-500 uppercase">{pmLabel(r.payment_method)}</span>
                </div>
                <div className="text-xs text-gray-300 truncate mt-0.5">{r.purchased_where}</div>
                <div className="text-[11px] text-gray-500 truncate">By {r.purchased_by} · {fmtDate(r.created_at?.slice(0, 10))}</div>
                {r.note && <div className="text-[11px] text-gray-600 truncate italic">{r.note}</div>}
                <div className="flex gap-1 mt-1.5">
                  <button onClick={() => setPreview(r)} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Preview" data-testid={`receipt-preview-${r.id}`}><Eye size={14} /></button>
                  <button onClick={() => downloadReceipt(r)} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Download" data-testid={`receipt-download-${r.id}`}><Download size={14} /></button>
                  {!locked && (
                    <>
                      <button onClick={() => setEditing(r)} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Edit" data-testid={`receipt-edit-${r.id}`}><Pencil size={14} /></button>
                      <button onClick={() => setDeleting(r)} className="text-gray-500 hover:text-red-400 p-1" title="Delete" data-testid={`receipt-delete-${r.id}`}><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ReceiptUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} projectId={project.id} onUploaded={changed} />
      <ReceiptPreviewDialog open={Boolean(preview)} onOpenChange={(o) => !o && setPreview(null)} receipt={preview} />
      <ReceiptEditDialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)} receipt={editing} onSaved={changed} />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="receipt-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this receipt?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              The receipt file and its {deleting ? centsToDollarString(deleting.amount_cents) : ''} Materials/Misc cost will be removed and project totals will recalculate. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="receipt-delete-confirm-btn">
              Delete Receipt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
